import { TelemetryLapData } from '../models/TelemetryData';

export type AlignMode = 'time' | 'dist';

/** Find the frame key in `keys` whose numeric value is closest to `dist`. */
function findClosestKey(keys: string[], dist: number): string {
  return keys.reduce((prev, curr) =>
    Math.abs(parseFloat(curr) - dist) < Math.abs(parseFloat(prev) - dist) ? curr : prev
  );
}

/**
 * Distance at which a lap's position dot should be drawn for a chart click.
 *
 * - DIST mode: every lap's dot sits at the clicked distance.
 * - TIME mode: the anchor lap's dot sits at the clicked distance; any other lap's
 *   dot sits at the distance where that lap was at the same *time* the anchor lap
 *   was at the clicked distance. Falls back to the clicked distance when the lap
 *   is the anchor, or when no anchor telemetry is available.
 */
export function resolveDotDistance(
  mode: AlignMode,
  anchorTelemetry: TelemetryLapData | null,
  lapTelemetry: TelemetryLapData,
  clickedDistance: number
): number {
  if (mode === 'dist') return clickedDistance;
  if (!anchorTelemetry || anchorTelemetry === lapTelemetry) return clickedDistance;

  const anchorKeys = Object.keys(anchorTelemetry.t);
  const anchorKey = findClosestKey(anchorKeys, clickedDistance);
  const anchorTime = anchorTelemetry.t[anchorKey]?.m;
  if (anchorTime == null) return clickedDistance;

  const lapKeys = Object.keys(lapTelemetry.t);
  let closestDist = clickedDistance;
  let closestDiff = Infinity;
  for (const k of lapKeys) {
    const diff = Math.abs(lapTelemetry.t[k].m - anchorTime);
    if (diff < closestDiff) {
      closestDiff = diff;
      closestDist = parseFloat(k);
    }
  }
  return closestDist;
}

export interface TrackMapPrefs {
  alignMode: AlignMode;
  anchorLapId: string | null;
}

const PREFS_KEY = 'telemetry:trackmap-prefs';
const DEFAULT_PREFS: TrackMapPrefs = { alignMode: 'time', anchorLapId: null };

/**
 * Resolve the concrete anchor lap id. `setAnchorId` is the user's explicit
 * choice (or null = use default). Returns the chosen lap when it is still among
 * `candidates`, else the first candidate (today's default), else null.
 */
export function resolveAnchorId(
  setAnchorId: string | null,
  candidates: string[]
): string | null {
  if (setAnchorId && candidates.includes(setAnchorId)) return setAnchorId;
  return candidates[0] ?? null;
}

export function loadTrackMapPrefs(): TrackMapPrefs {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (!raw) return { ...DEFAULT_PREFS };
    const parsed = JSON.parse(raw);
    const alignMode: AlignMode = parsed.alignMode === 'dist' ? 'dist' : 'time';
    const anchorLapId = typeof parsed.anchorLapId === 'string' ? parsed.anchorLapId : null;
    return { alignMode, anchorLapId };
  } catch {
    return { ...DEFAULT_PREFS };
  }
}

export function saveTrackMapPrefs(prefs: TrackMapPrefs): void {
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
  } catch {
    /* ignore quota / unavailable storage */
  }
}
